import React, { useState } from 'react';
import prestacaoContaService, { type PrestacaoComprovantePagamento } from '../../services/prestacaoContaService';
import { bodyTextClass, formatCurrencyInput, inputClass, labelTextClass, parseCurrencyInput, primaryButtonClass } from './shared';
import { toUpperText } from '../../utils/formatters';

// Texto livre persistido; CPF/CNPJ, datas e numeroTransferencia (código emitido pelo banco) ficam como digitados.
const CAMPOS_EM_MAIUSCULAS: ReadonlySet<keyof PrestacaoComprovantePagamento> = new Set(['credorNome', 'numeroDocumentoFiscal', 'numeroTransferencia']);

type FormState = PrestacaoComprovantePagamento & {
  valor: string; observacoes: string; file: File | null;
  comprovanteDespesa: File | null; controleAbastecimento: File | null;
};
type Props = {
  pcId: string; itemExecucaoId: string; podeAdicionarComprovante: boolean;
  disabledMessage?: string; onAdicionado?: () => void;
};
const vazio: FormState = { valor: '', observacoes: '', file: null, comprovanteDespesa: null, controleAbastecimento: null, fonteRecurso: '1' };

const ComprovanteForm: React.FC<Props> = ({ pcId, itemExecucaoId, podeAdicionarComprovante, disabledMessage, onAdicionado }) => {
  const [form, setForm] = useState<FormState>(vazio);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const update = (patch: Partial<FormState>) => setForm(prev => ({ ...prev, ...patch }));
  const etapa = Boolean(itemExecucaoId);
  const adicionar = async (event: React.FormEvent) => {
    event.preventDefault();
    // TEMPORARIO: exigencia de anexos desabilitada a pedido do usuario (sessao 2026-09-18)
    // para destravar o cadastro de valores por etapa sem os documentos em maos ainda.
    // Reverter junto com PrestacaoContaService.criarPagamento antes de ir a producao.
    const valor = parseCurrencyInput(form.valor);
    if (!Number.isFinite(valor) || valor <= 0) {
      setError('Informe um valor maior que zero.'); return;
    }
    if (form.dataPagamento && form.dataDocumentoFiscal && form.dataPagamento < form.dataDocumentoFiscal) {
      setError('A data de pagamento não pode ser anterior à emissão do documento.'); return;
    }
    setSaving(true); setError(null);
    try {
      await prestacaoContaService.criarComprovante(pcId, { ...form, valor, file: form.file, itemExecucaoId });
      setForm(vazio); setFileKey(k => k + 1); onAdicionado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao cadastrar pagamento.');
    } finally { setSaving(false); }
  };
  const campo = (key: keyof PrestacaoComprovantePagamento, label: string, type = 'text', maxLength?: number) => (
    <label className={`block text-sm ${labelTextClass}`}>
      {label}
      <input type={type} className={inputClass} required={etapa} maxLength={maxLength}
        value={String(form[key] || '')} onChange={e => update({ [key]: CAMPOS_EM_MAIUSCULAS.has(key) ? toUpperText(e.target.value) : e.target.value })}
        pattern={key === 'credorDocumento' ? '[0-9./ \\-]{11,18}' : undefined} />
    </label>
  );
  const anexo = (key: 'file' | 'comprovanteDespesa' | 'controleAbastecimento', label: string, help: string, required: boolean) => (
    <label className={`block space-y-1 text-sm ${bodyTextClass}`}>
      <span className="font-medium">{label}</span>
      <span className={`block text-xs ${labelTextClass}`}>{help}</span>
      <input key={`${fileKey}-${key}`} type="file" required={required} className="block w-full text-sm"
        onChange={e => update({ [key]: e.target.files?.[0] || null })} />
    </label>
  );
  return (
    <form onSubmit={adicionar} className="space-y-4">
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-300">{error}</p>}
      <fieldset disabled={!podeAdicionarComprovante || saving} className="space-y-4 disabled:opacity-60">
        <legend className={`mb-3 font-semibold ${bodyTextClass}`}>Dados do pagamento</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={`text-sm ${labelTextClass}`}>Fonte do recurso
            <select className={inputClass} value={form.fonteRecurso} onChange={e => update({ fonteRecurso: e.target.value })}>
              <option value="1">Prefeitura</option><option value="2">Recurso próprio (contrapartida)</option>
            </select>
            <span className="mt-1 block text-xs">Declare a origem efetivamente utilizada neste pagamento, mesmo que seja diferente do valor previsto no plano.</span>
          </label>
          {campo('credorNome', 'Credor', 'text', 200)}
          {campo('credorDocumento', 'CNPJ/CPF do credor', 'text', 18)}
          {campo('numeroDocumentoFiscal', 'Nº do Documento', 'text', 64)}
          {campo('dataDocumentoFiscal', 'Data de Emissão do Documento', 'date')}
          {campo('numeroTransferencia', 'Nº da transferência bancária', 'text', 100)}
          {campo('dataPagamento', 'Data de Pagamento', 'date')}
          <label className={`text-sm ${labelTextClass}`}>Valor pago (R$)
            <input type="text" inputMode="numeric" required placeholder="R$ 0,00" className={inputClass}
              value={form.valor} onChange={e => update({ valor: formatCurrencyInput(e.target.value) })} />
          </label>
        </div>
        <label className={`block text-sm ${labelTextClass}`}>Observações
          <textarea className={inputClass} rows={2} value={form.observacoes} onChange={e => update({ observacoes: toUpperText(e.target.value) })} />
        </label>
        {anexo('file', 'Comprovante de Pagamento — Identificação do Beneficiário Final', 'Decreto 325/2024, artigo 58.', false)}
        {etapa && <>
          {anexo('comprovanteDespesa', 'Cópia do Comprovante de Despesa', 'Notas fiscais com ateste de recebido; holerites assinados pelo funcionário (Decreto 325/2024, artigo 70, alínea X).', false)}
          <label className={`flex items-center gap-2 text-sm ${bodyTextClass}`}>
            <input type="checkbox" checked={Boolean(form.despesaCombustivel)} onChange={e => update({ despesaCombustivel: e.target.checked, controleAbastecimento: null })} />
            Despesa com combustível prevista na etapa (carro oficial)
          </label>
          {form.despesaCombustivel && anexo('controleAbastecimento', 'Controle de abastecimento com quilometragem', 'Apresentar quilometragem na nota fiscal do carro oficial (Decreto 325/2024, artigo 70, § 1º, alínea X).', false)}
        </>}
        <button type="submit" className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${primaryButtonClass}`}>
          {saving ? 'Enviando...' : 'Adicionar comprovante'}
        </button>
      </fieldset>
      {!podeAdicionarComprovante && disabledMessage && <p className={`text-xs ${labelTextClass}`}>{disabledMessage}</p>}
    </form>
  );
};
export default ComprovanteForm;
